import React, { useEffect, useRef } from 'react';

import AlertColoredOutlinedIcon from '@travel/design-system/assets/icons/AlertColoredOutlinedIcon';
import CloseOutlineIcon from '@travel/design-system/assets/icons/CloseOutlineIcon';
import ErrorColoredOutlinedIcon from '@travel/design-system/assets/icons/ErrorColoredOutlinedIcon';
import InfoColoredOutlinedIcon from '@travel/design-system/assets/icons/InfoColoredOutlinedIcon';
import SuccessColoredOutlinedIcon from '@travel/design-system/assets/icons/SuccessColoredOutlinedIcon';

import {
  type NotificationBannerStates,
  NotificationBannerToastTimers,
  type NotificationBannerTypes,
  type NotificationBannerVariants,
} from '../../types/variants';
import cx from '../../utils/classNames';
import toCamelCase from '../../utils/toCamelCase';

import styles from './notificationBanner.module.scss';

export type NotificationBannerProps = {
  /** types of the banner */
  type?: NotificationBannerTypes;
  /** variants of the banner */
  variant?: NotificationBannerVariants;
  /** State type that determines color scheme */
  state?: NotificationBannerStates;

  /** Icon element to display (React node for flexibility) */
  icon?: React.ReactNode;

  /** Title text of the notification */
  title?: React.ReactNode;

  /** Description/message text */
  message?: React.ReactNode;

  /** Action button text or element */
  action?: React.ReactNode;

  /** Whether to show the close button */
  showClose?: boolean;

  /** Handler for action button click */
  onActionClick?: () => void;

  /** Controls visibility of toast notification (only applies when type='toast') */
  showToast?: boolean;

  /** Callback to update toast visibility state */
  setShowToast?: (showToast: boolean) => void;

  /** Auto-dismiss duration for toast: 'short' (4s) or 'long' (8s) */
  toastTimer?: NotificationBannerToastTimers;

  /** Handler for close button click */
  onClose?: () => void;
} & Omit<React.HTMLAttributes<HTMLDivElement>, 'title'>;

const getDefaultIcon = (state: NotificationBannerStates) => {
  switch (state) {
    case 'success':
      return <SuccessColoredOutlinedIcon size={14} />;
    case 'error':
      return <ErrorColoredOutlinedIcon size={14} />;
    case 'warning':
      return <AlertColoredOutlinedIcon size={14} />;
    case 'info':
      return <InfoColoredOutlinedIcon size={14} />;
    default:
      return null;
  }
};

export const NotificationBanner: React.FC<NotificationBannerProps> = ({
  className,
  type = 'inline',
  variant = 'low',
  state = 'info',
  icon,
  title,
  message,
  action,
  onActionClick,
  onClose,
  showToast,
  setShowToast,
  toastTimer = 'short',
  showClose = true,
  ...rest
}) => {
  const rootRef = useRef<HTMLDivElement>(null);

  // Use provided icon or default based on state
  const displayIcon = icon !== undefined ? icon : getDefaultIcon(state as NotificationBannerStates);

  // Build class names with variant and state combined
  const rootClassNames = cx(
    styles.root,
    variant && state && styles[toCamelCase('root', variant, state)],
    variant && styles[toCamelCase('root', variant)],
    type && styles[toCamelCase('root', type)],
    className,
  );

  const iconClassNames = cx(styles.icon);

  const titleClassNames = cx(styles.title);

  const messageClassNames = cx(styles.message);

  const actionClassNames = cx(
    styles.action,
    variant ? styles[toCamelCase('action', variant)] : '',
    type ? styles[toCamelCase('action', type)] : '',
  );

  const closeClassNames = cx(
    styles.close,
    variant ? styles[toCamelCase('close', variant)] : '',
    type ? styles[toCamelCase('action', type)] : '',
  );
  useEffect(() => {
    if (!showToast || !setShowToast || !rootRef.current) return;

    const timer = toastTimer === NotificationBannerToastTimers.Long ? 8000 : 4000;
    const element = rootRef.current;

    // Remove fadeOut class when toast is shown
    element.classList.remove(styles.fadeOut);

    // Start auto-dismiss timer
    const dismissTimer = setTimeout(() => {
      // First trigger fade out animation by adding class
      element.classList.add(styles.fadeOut);

      // Then hide toast after animation completes (300ms)
      setTimeout(() => {
        setShowToast(false);
      }, 300);
    }, timer);

    return () => clearTimeout(dismissTimer);
  }, [showToast, setShowToast, toastTimer]);
  return (
    <>
      {(type !== 'toast' || (type === 'toast' && showToast)) && (
        <div ref={rootRef} className={rootClassNames} role="alert" {...rest}>
          {displayIcon && (
            <span className={iconClassNames} aria-hidden="true">
              {displayIcon}
            </span>
          )}

          <div className={styles.content}>
            {title && <div className={titleClassNames}>{title}</div>}
            {message && <div className={messageClassNames}>{message}</div>}
          </div>
          {(action || (type !== 'callout' && showClose)) && (
            <div className={styles.actions}>
              {action && (
                <button
                  type="button"
                  className={actionClassNames}
                  onClick={onActionClick}
                  aria-label="Action"
                >
                  {action}
                </button>
              )}

              {type !== 'callout' && showClose && (
                <button
                  type="button"
                  className={closeClassNames}
                  onClick={onClose}
                  aria-label="Close notification"
                >
                  <CloseOutlineIcon size={16} />
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default NotificationBanner;
